import React, { useState, useEffect } from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
} from "./config/authClient";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  writeBatch,
} from "./config/dataClient";
import {
  Clock,
  Calculator,
  History,
  Settings,
  Briefcase,
  User,
  FileText,
  DollarSign,
  Save,
  Plus,
  Trash2,
  CreditCard,
  Leaf
} from "lucide-react";

import { auth, db } from "./config/firebase";
import { isLocalPreview } from "./config/previewMode";
import { previewUsers, selectPreviewUser } from "./config/previewStore";
import { APP_ID, ROLES } from "./config/constants";
import LoginScreen from "./components/ui/LoginScreen";
import NavButton from "./components/ui/NavButton";
import SeasonalDecorations from "./components/ui/SeasonalDecorations";
import RocDateInput from "./components/ui/RocDateInput";
import { ToastConfirmProvider } from "./components/ui/ToastConfirm";

import HomeView from "./components/views/HomeView";
// 移除 TeamView 引用
import HoursView from "./components/views/HoursView";
import HistoryView from "./components/views/HistoryView";
import SettingsView from "./components/views/SettingsView";
import LeaveView from "./components/views/LeaveView";
import PayrollView from "./components/views/PayrollView";

// --- 新員工強制填寫資料表單 ---
const OnboardingForm = ({ user, initialData, onComplete }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    gender: "",
    idNumber: "",
    bloodType: "",
    birthday: "",
    mobile: "",
    address: "",
    personalEmail: initialData?.email || "",
    education: "",
    workExperience: [{}], 
    familyMembers: [{}], 
    emergencyContact: { name: "", relation: "", mobile: "" },
    bankAccount: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (arrayName, index, field, value) => {
    const newArr = [...formData[arrayName]];
    if (!newArr[index]) newArr[index] = {};
    newArr[index][field] = value;
    setFormData(prev => ({ ...prev, [arrayName]: newArr }));
  };

  const handleObjectChange = (objName, field, value) => {
    setFormData(prev => ({ ...prev, [objName]: { ...prev[objName], [field]: value } }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const requiredFields = [
      { key: "fullName", label: "中文全名" },
      { key: "gender", label: "性別" },
      { key: "idNumber", label: "身份證字號" },
      { key: "bloodType", label: "血型" },
      { key: "birthday", label: "出生年月日" },
      { key: "mobile", label: "手機號碼" },
      { key: "address", label: "通訊地址" },
      { key: "personalEmail", label: "Email" },
      { key: "education", label: "畢業學校及科系" },
      { key: "bankAccount", label: "銀行帳號" }
    ];

    for (const field of requiredFields) {
      if (!formData[field.key] || formData[field.key].trim() === "") {
        alert(`請填寫「${field.label}」`);
        return;
      }
    }

    if (formData.workExperience.length === 0) {
        alert("請至少填寫一項工作經歷");
        return;
    }
    for (let i = 0; i < formData.workExperience.length; i++) {
        const w = formData.workExperience[i];
        if (!w.company || !w.job || !w.period) {
            alert(`請完整填寫第 ${i + 1} 項工作經歷的所有欄位`);
            return;
        }
    }

    if (formData.familyMembers.length === 0) {
        alert("請至少填寫一位家庭成員");
        return;
    }
    for (let i = 0; i < formData.familyMembers.length; i++) {
        const f = formData.familyMembers[i];
        if (!f.name || !f.title) {
            alert(`請完整填寫第 ${i + 1} 位家庭成員的所有欄位`);
            return;
        }
    }

    const { name, relation, mobile } = formData.emergencyContact;
    if (!name || !relation || !mobile) {
      alert("請完整填寫緊急聯絡人資訊 (姓名、關係、手機)");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, "artifacts", APP_ID, "users", user.uid, "profile", "main"), {
        ...formData,
        isProfileFilled: true,
        name: formData.fullName
      });

      batch.update(doc(db, "artifacts", APP_ID, "public", "data", "daily_status", user.uid), {
        name: formData.fullName,
        fullName: formData.fullName,
        birthday: formData.birthday || "",
      });
      await batch.commit();
      onComplete({
        ...formData,
        isProfileFilled: true,
        name: formData.fullName,
      });
    } catch (e) {
      console.error(e);
      alert("儲存失敗，請重試");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-slate-100 overflow-y-auto">
      <div className="max-w-md mx-auto w-full bg-white min-h-screen shadow-xl p-6 pb-20">
        <div className="text-center mb-6">
          <Briefcase className="w-12 h-12 text-blue-600 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-slate-800">歡迎加入！</h2>
          <p className="text-slate-500 text-sm">初次使用請先完善您的人事資料 (所有欄位必填)</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-600 block mb-1">中文全名 <span className="text-red-500">*</span></label>
            <input className="w-full border rounded p-2 text-sm" value={formData.fullName} onChange={e=>handleChange("fullName", e.target.value)} />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
             <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">性別 <span className="text-red-500">*</span></label>
                <select className="w-full border rounded p-2 text-sm bg-white" value={formData.gender} onChange={e=>handleChange("gender", e.target.value)}>
                    <option value="">請選擇</option>
                    <option value="male">男</option>
                    <option value="female">女</option>
                    <option value="other">其他</option>
                </select>
             </div>
             <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">血型 <span className="text-red-500">*</span></label>
                <select className="w-full border rounded p-2 text-sm bg-white" value={formData.bloodType} onChange={e=>handleChange("bloodType", e.target.value)}>
                    <option value="">請選擇</option>
                    <option value="A">A型</option>
                    <option value="B">B型</option>
                    <option value="O">O型</option>
                    <option value="AB">AB型</option>
                    <option value="Unknown">未知</option>
                </select>
             </div>
          </div>

          <div>
             <label className="text-xs font-bold text-slate-600 block mb-1">身份證字號 <span className="text-red-500">*</span></label>
             <input className="w-full border rounded p-2 text-sm" value={formData.idNumber} onChange={e=>handleChange("idNumber", e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
             <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">出生年月日（民國） <span className="text-red-500">*</span></label>
                <RocDateInput required commitOnBlur={false} className="w-full border rounded p-2 text-sm" value={formData.birthday} onChange={value=>handleChange("birthday", value)} />
             </div>
             <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">手機號碼 <span className="text-red-500">*</span></label>
                <input className="w-full border rounded p-2 text-sm" value={formData.mobile} onChange={e=>handleChange("mobile", e.target.value)} />
             </div>
          </div>

          <div>
             <label className="text-xs font-bold text-slate-600 block mb-1">通訊地址 <span className="text-red-500">*</span></label>
             <input className="w-full border rounded p-2 text-sm" value={formData.address} onChange={e=>handleChange("address", e.target.value)} />
          </div>
          
          <div>
             <label className="text-xs font-bold text-slate-600 block mb-1">Email <span className="text-red-500">*</span></label>
             <input type="email" className="w-full border rounded p-2 text-sm" value={formData.personalEmail} onChange={e=>handleChange("personalEmail", e.target.value)} />
          </div>

          <div>
             <label className="text-xs font-bold text-slate-600 block mb-1">畢業學校及科系 <span className="text-red-500">*</span></label>
             <input className="w-full border rounded p-2 text-sm" placeholder="例：台灣大學 資訊工程系" value={formData.education} onChange={e=>handleChange("education", e.target.value)} />
          </div>

          {/* 銀行帳戶資料 */}
          <div className="bg-slate-50 p-3 rounded border border-slate-200">
             <div className="text-xs font-bold text-slate-600 mb-2 flex items-center gap-1"><CreditCard size={14}/> 銀行帳戶資料 <span className="text-red-500">*</span></div>
             <div className="grid grid-cols-2 gap-3">
                 <div>
                    <label className="text-[10px] text-slate-400 block mb-1">銀行名稱</label>
                    <input className="w-full border rounded p-1.5 text-xs bg-slate-100 text-slate-500 font-bold" value="國泰世華 013" disabled/>
                 </div>
                 <div>
                    <label className="text-[10px] text-slate-400 block mb-1">戶名 (同全名)</label>
                    <input className="w-full border rounded p-1.5 text-xs bg-slate-100 text-slate-500" value={formData.fullName || "請先填寫全名"} disabled/>
                 </div>
                 <div className="col-span-2">
                    <label className="text-[10px] text-slate-600 block mb-1 font-bold">銀行帳號</label>
                    <input className="w-full border rounded p-1.5 text-xs focus:ring-2 focus:ring-blue-100" placeholder="請輸入帳號" value={formData.bankAccount} onChange={e=>handleChange("bankAccount", e.target.value)}/>
                 </div>
             </div>
          </div>

          {/* 工作經歷 */}
          <div className="bg-slate-50 p-3 rounded border">
             <div className="flex justify-between mb-2">
                <div className="text-xs font-bold text-slate-600">工作經歷 <span className="text-red-500">*</span></div>
                <button onClick={() => setFormData(p => ({...p, workExperience: [...p.workExperience, {}]}))} className="text-blue-500 text-xs flex items-center"><Plus size={12}/> 新增</button>
             </div>
             {formData.workExperience.map((w, i) => (
                <div key={i} className="mb-3 p-2 bg-white border border-slate-200 rounded relative shadow-sm">
                   <button onClick={() => {
                       const newArr = [...formData.workExperience];
                       newArr.splice(i, 1);
                       setFormData(p => ({...p, workExperience: newArr}));
                   }} className="absolute top-2 right-2 text-slate-400 hover:text-red-500"><Trash2 size={14}/></button>
                   
                   <div className="space-y-2 pr-6">
                       <div>
                           <label className="text-[10px] text-slate-400 block">工作機構</label>
                           <input className="w-full border rounded p-1.5 text-xs" placeholder="機構名稱" value={w.company || ""} onChange={e=>handleArrayChange("workExperience", i, "company", e.target.value)}/>
                       </div>
                       <div className="flex gap-2">
                           <div className="flex-1">
                               <label className="text-[10px] text-slate-400 block">職別</label>
                               <input className="w-full border rounded p-1.5 text-xs" placeholder="職稱" value={w.job || ""} onChange={e=>handleArrayChange("workExperience", i, "job", e.target.value)}/>
                           </div>
                           <div className="flex-1">
                               <label className="text-[10px] text-slate-400 block">任職期間</label>
                               <input className="w-full border rounded p-1.5 text-xs" placeholder="起訖時間" value={w.period || ""} onChange={e=>handleArrayChange("workExperience", i, "period", e.target.value)}/>
                           </div>
                       </div>
                   </div>
                </div>
             ))}
          </div>

          <div className="bg-slate-50 p-3 rounded border">
             <div className="flex justify-between mb-2">
                <div className="text-xs font-bold text-slate-600">家庭成員 <span className="text-red-500">*</span></div>
                <button onClick={() => setFormData(p => ({...p, familyMembers: [...p.familyMembers, {}]}))} className="text-blue-500 text-xs flex items-center"><Plus size={12}/> 新增</button>
             </div>
             {formData.familyMembers.map((f, i) => (
                <div key={i} className="flex gap-2 mb-2 items-center">
                   <input className="w-20 border rounded p-1.5 text-xs" placeholder="姓名" value={f.name || ""} onChange={e=>handleArrayChange("familyMembers", i, "name", e.target.value)}/>
                   <input className="flex-1 border rounded p-1.5 text-xs" placeholder="稱謂" value={f.title || ""} onChange={e=>handleArrayChange("familyMembers", i, "title", e.target.value)}/>
                   <button onClick={() => {
                       const newArr = [...formData.familyMembers];
                       newArr.splice(i, 1);
                       setFormData(p => ({...p, familyMembers: newArr}));
                   }} className="text-red-400"><Trash2 size={12}/></button>
                </div>
             ))}
          </div>

          <div className="bg-slate-50 p-3 rounded border">
             <div className="text-xs font-bold text-slate-600 mb-2">緊急聯絡人 <span className="text-red-500">*</span></div>
             <div className="flex gap-2">
                <input className="w-20 border rounded p-1.5 text-xs" placeholder="姓名" value={formData.emergencyContact.name} onChange={e=>handleObjectChange("emergencyContact", "name", e.target.value)}/>
                <input className="w-20 border rounded p-1.5 text-xs" placeholder="關係" value={formData.emergencyContact.relation} onChange={e=>handleObjectChange("emergencyContact", "relation", e.target.value)}/>
                <input className="flex-1 border rounded p-1.5 text-xs" placeholder="手機" value={formData.emergencyContact.mobile} onChange={e=>handleObjectChange("emergencyContact", "mobile", e.target.value)}/>
             </div>
          </div>

          <button onClick={handleSubmit} disabled={isSubmitting} className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold shadow-lg mt-4 flex justify-center items-center gap-2 disabled:bg-slate-300 disabled:cursor-not-allowed">
             <Save size={18}/> {isSubmitting ? "儲存中..." : "儲存並開始使用"}
          </button>
        </div>
      </div>
    </div>
  );
};


export default function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("home");
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState(ROLES.PARTTIME);
  const [accountStatus, setAccountStatus] = useState("pending");
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  useEffect(() => {
    if (!user || !userProfile || accountStatus !== "active" || userProfile.isHidden) return;
    import("./utils/leaveNotifications").then(({ registerNotificationContact }) =>
      registerNotificationContact(user)
    ).catch(() => console.warn("通知 Email 尚未同步"));
  }, [user, userProfile, accountStatus]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setIsAdmin(false);
        const docRef = doc(
          db,
          "artifacts",
          APP_ID,
          "users",
          u.uid,
          "profile",
          "main"
        );
        try {
          const docSnap = await getDoc(docRef);

          const settingsRef = doc(
            db,
            "artifacts",
            APP_ID,
            "public",
            "data",
            "settings",
            "main"
          );
          const settingsSnap = await getDoc(settingsRef);
          const isSuperAdmin =
            settingsSnap.exists() &&
            settingsSnap.data().adminUIDs?.includes(u.uid);

          if (docSnap.exists()) {
            const data = docSnap.data();
            let currentStatus = data.accountStatus || "active";

            if (!data.isProfileFilled && !isSuperAdmin) {
                setNeedsOnboarding(true);
            } else {
                setNeedsOnboarding(false);
            }

            // [優化] 同步寫入冗餘欄位 (fullName/birthday) 到 daily_status，避免列表頁 N+1 查詢
            const denormalized = {
              fullName: data.fullName || data.name || "",
              birthday: data.birthday || "",
            };

            // 已封存帳號維持停用：不再自動復原為 pending、也不寫回 DB
            // （避免離職者重新登入即自我恢復）。下方會以 accountStatus:"archived" 顯示停用畫面。
            if (!(currentStatus === "archived" && !isSuperAdmin)) {
              const dailyRef = doc(
                db,
                "artifacts",
                APP_ID,
                "public",
                "data",
                "daily_status",
                u.uid
              );
              const dailySnap = await getDoc(dailyRef);
              if (!dailySnap.exists()) {
                await setDoc(dailyRef, {
                  uid: u.uid,
                  name: data.name,
                  photoURL: data.photoURL,
                  status: "offline",
                  isHidden: false,
                  accountStatus: currentStatus,
                  role: data.role || ROLES.PARTTIME,
                  ...denormalized,
                });
              } else {
                  await setDoc(dailyRef, {
                    role: data.role || ROLES.PARTTIME,
                    ...denormalized,
                  }, { merge: true });
              }
            }

            setUserProfile(data);
            setRole(data.role || ROLES.PARTTIME);
            setAccountStatus(isSuperAdmin ? "active" : currentStatus);

            if (isSuperAdmin) {
              setIsAdmin(true);
              setRole(ROLES.ADMIN);
            }
          } else {
            const initialStatus = isSuperAdmin ? "active" : "pending";

            const profileData = {
              name: u.displayName || "未命名用戶",
              photoURL: u.photoURL || null,
              email: u.email,
              role: ROLES.PARTTIME,
              accountStatus: initialStatus,
              createdAt: serverTimestamp(),
              isProfileFilled: false 
            };

            await setDoc(docRef, profileData);
            await setDoc(
              doc(
                db,
                "artifacts",
                APP_ID,
                "public",
                "data",
                "daily_status",
                u.uid
              ),
              {
                uid: u.uid,
                name: profileData.name,
                photoURL: profileData.photoURL,
                status: "offline",
                isHidden: false,
                accountStatus: initialStatus,
                role: ROLES.PARTTIME
              }
            );

            setUserProfile(profileData);
            setRole(ROLES.PARTTIME);
            setAccountStatus(initialStatus);
            if (isSuperAdmin) {
                setIsAdmin(true);
            } else {
                setNeedsOnboarding(true); 
            }
          }
        } catch (e) {
          console.error("Profile load error:", e);
        }
        setUser(u);
      } else {
        setUser(null);
        setUserProfile(null);
        setIsAdmin(false);
        setRole(null);
        setAccountStatus("pending");
        setNeedsOnboarding(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    if (!auth) return;
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Login Error:", error);
      if (
        error.code === "auth/popup-blocked" ||
        error.code === "auth/cancelled-popup-request" ||
        error.code === "auth/popup-closed-by-user"
      ) {
        alert("登入視窗被阻擋或已關閉。請使用 Chrome 或 Edge 開啟此預覽，允許彈出視窗後重新登入。");
        return;
      }
      if (error.code === "auth/unauthorized-domain") {
        alert("此預覽網址尚未獲准登入。請在 Firebase Authentication 的授權網域中加入 localhost。");
        return;
      }
      alert("登入失敗: " + error.message);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen text-slate-500">
        載入中...
      </div>
    );
  if (!user) return <LoginScreen onLogin={handleLogin} />;

  if (needsOnboarding) {
      return <OnboardingForm user={user} initialData={userProfile} onComplete={(updated) => {
          setUserProfile((prev) => ({ ...(prev || {}), ...updated }));
          setNeedsOnboarding(false);
      }} />;
  }

  if (accountStatus === "pending") {
    return (
      <div className="flex flex-col h-screen bg-slate-100 justify-center items-center p-6 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-sm">
          <User size={48} className="mx-auto text-orange-400 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">帳號待審核</h2>
          <p className="text-slate-500 text-sm mb-6">
            您的帳號正在等待管理員授權。
            <br />
            請通知管理員啟用您的權限。
          </p>
          <div className="text-xs text-slate-400 mb-4">
            UID: {user.uid.slice(0, 5)}...
          </div>
          <button
            onClick={() => auth.signOut()}
            className="text-blue-600 font-bold text-sm hover:underline"
          >
            登出切換帳號
          </button>
        </div>
      </div>
    );
  }

  // 已封存、或已隱藏（＝離職）的帳號一律停用；超級管理員不受此限
  if (accountStatus === "archived" || (!isAdmin && userProfile?.isHidden)) {
    return (
      <div className="flex flex-col h-screen bg-slate-100 justify-center items-center p-6 text-center">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-sm">
          <User size={48} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">帳號已停用</h2>
          <p className="text-slate-500 text-sm mb-6">
            此帳號已被停用封存。若需重新啟用，請洽管理員。
          </p>
          <button
            onClick={() => auth.signOut()}
            className="text-blue-600 font-bold text-sm hover:underline"
          >
            重新登入
          </button>
        </div>
      </div>
    );
  }

  const canAccessLeave = role === ROLES.FULLTIME || role === ROLES.ADMIN;

  return (
    <ToastConfirmProvider>
    <div className="autumn-theme halloween-theme november-theme app-shell flex flex-col h-screen bg-slate-100 font-sans text-slate-900 max-w-md mx-auto shadow-2xl overflow-hidden relative">
      <header className="glass-header bg-white p-4 shadow-sm z-10 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Leaf className="brand-leaf w-6 h-6" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-bold text-slate-800">Urtale上班系統</h1>
            <p className="season-caption">深秋森林・十一月值勤簿</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full overflow-hidden bg-slate-100">
            {userProfile?.photoURL ? (
              <img
                src={userProfile.photoURL}
                alt="Me"
                className="w-full h-full object-cover"
              />
            ) : (
              <User size={24} className="p-1" />
            )}
          </div>
          <div className="text-xs text-slate-500 flex flex-col items-end">
            <span className="font-bold text-blue-600">{userProfile?.name}</span>
            <span className="text-[10px] text-slate-400">
              {role === ROLES.ADMIN
                ? "管理員"
                : role === ROLES.FULLTIME
                ? "正職"
                : "工讀生"}
            </span>
          </div>
        </div>
      </header>

      {isLocalPreview && (
        <div className="preview-toolbar relative z-10 shrink-0 flex items-center justify-between gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs">
          <span className="text-amber-800 font-bold">本機預覽</span>
          <select aria-label="預覽身分" value={user.uid} onChange={e => { setLoading(true); selectPreviewUser(e.target.value); }} className="border border-amber-200 rounded px-2 py-1 bg-white text-slate-700 max-w-[180px]">
            {previewUsers.map(person => <option key={person.uid} value={person.uid}>{person.name}</option>)}
          </select>
        </div>
      )}

      <SeasonalDecorations />
      <main className="flex-1 overflow-y-auto pb-20 scrollbar-hide">
        {activeTab === "home" && (
          <HomeView user={user} userProfile={userProfile} />
        )}
        
        {/* 移除了 TeamView 的渲染 */}

        {activeTab === "hours" && (
          <HoursView user={user} isAdmin={isAdmin || role === ROLES.ADMIN} role={role} />
        )}
        {activeTab === "history" && <HistoryView user={user} />}

        {activeTab === "leave" && canAccessLeave && (
          <LeaveView user={user} userProfile={userProfile} role={role} />
        )}

        {activeTab === "payroll" && (
          <PayrollView user={user} userProfile={userProfile} role={role} />
        )}

        {activeTab === "settings" && (
          <SettingsView user={user} userProfile={userProfile} role={role} />
        )}
      </main>

      <nav
        className="glass-nav absolute bottom-0 w-full bg-white border-t border-slate-200 flex justify-around py-2 px-1 pb-safe"
        style={{ zIndex: 50 }}
      >
        <NavButton
          active={activeTab === "home"}
          onClick={() => setActiveTab("home")}
          icon={Clock}
          label="打卡"
        />
        
        {/* 移除了 團隊 按鈕 */}

        <NavButton
          active={activeTab === "hours"}
          onClick={() => setActiveTab("hours")}
          icon={Calculator}
          label="工時"
        />
        <NavButton
          active={activeTab === "history"}
          onClick={() => setActiveTab("history")}
          icon={History}
          label="紀錄"
        />

        {canAccessLeave && (
          <NavButton
            active={activeTab === "leave"}
            onClick={() => setActiveTab("leave")}
            icon={FileText}
            label="請假"
          />
        )}

        <NavButton
          active={activeTab === "payroll"}
          onClick={() => setActiveTab("payroll")}
          icon={DollarSign}
          label="薪資"
        />
        <NavButton
          active={activeTab === "settings"}
          onClick={() => setActiveTab("settings")}
          icon={Settings}
          label="設定"
        />
      </nav>
    </div>
    </ToastConfirmProvider>
  );
}
